
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { BackButton } from "@/components/BackButton";
import { useTheme } from "@/contexts/ThemeContext";
import { motion } from "framer-motion";

const PrivacyPolicy = () => {
  const { colors } = useTheme();

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ backgroundColor: colors.background }}>
      <Navigation />
      <div className="pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          <BackButton />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg max-w-none"
            style={{ color: colors.foreground }}
          >
            <h1 className="text-4xl font-bold mb-8" style={{ color: colors.foreground }}>
              Privacy Policy
            </h1>
            
            <p className="text-sm mb-8" style={{ color: colors.secondary }}>
              Effective Date: June 23, 2025
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                1. Introduction
              </h2>
              <p style={{ color: colors.secondary }}>
                Agape Entertainment is committed to protecting your personal information and ensuring transparency about how we handle your data. This Privacy Policy outlines how we collect, use, retain, and protect your information in accordance with applicable data protection laws, including the Digital Personal Data Protection Act (India) and GDPR. By engaging with our services or visiting our website, you consent to the practices described here.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                2. Definitions
              </h2>
              <p style={{ color: colors.secondary }}>
                For clarity, the term "Personal Data" refers to information that identifies you directly or indirectly, such as your name, email address, or phone number. "Usage Data" refers to technical and analytical information that is collected automatically when you interact with our services, such as your IP address, browser type, or usage patterns.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                3. Data We Collect
              </h2>
              <p style={{ color: colors.secondary }}>
                We collect personal and usage data when you interact with us—whether by visiting our website, filling out a form, or using our services. This may include your name, contact details, location data, device information, and preferences. Some of this data is collected through cookies or analytics tools like Google Analytics to help us understand user behavior and improve our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                4. Legal Basis
              </h2>
              <p style={{ color: colors.secondary }}>
                Your data is collected and processed based on one or more legal grounds, such as your consent, the need to fulfill a contract, compliance with legal obligations, or our legitimate business interests like service improvement and security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                5. How We Use Your Data
              </h2>
              <p style={{ color: colors.secondary }}>
                The data we collect is used to provide, maintain, and enhance our services, respond to your inquiries, deliver personalized experiences, manage our communications with you, analyze platform usage, and ensure legal compliance. Your data also helps us detect fraud, ensure security, and maintain the quality of our content and services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                6. Data Retention
              </h2>
              <p style={{ color: colors.secondary }}>
                We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy. After that, we either securely delete or anonymize your information, ensuring that it cannot be linked back to you.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                7. Security Measures
              </h2>
              <p style={{ color: colors.secondary }}>
                To protect your data, we implement industry-standard security measures including encryption, firewalls, secure access controls, and regular system audits. Although no system can guarantee absolute security, we take every reasonable step to protect your personal information from unauthorized access or disclosure.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                8. Children's Privacy
              </h2>
              <p style={{ color: colors.secondary }}>
                Our services are not designed for children under the age of 13. We do not knowingly collect data from minors. If we become aware that a child has submitted personal information, we will promptly delete it. Parents or guardians are encouraged to monitor and guide their children's online activity.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                9. Sharing Data
              </h2>
              <p style={{ color: colors.secondary }}>
                We may share your personal data with trusted third-party service providers who support us in delivering our services, such as hosting partners, analytics providers, and payment gateways. These third parties are bound by confidentiality agreements and are only authorized to use your data for specific tasks on our behalf. We may also disclose information if required by law or in response to legal requests.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                10. Cross-Border Transfers
              </h2>
              <p style={{ color: colors.secondary }}>
                If your data is transferred outside of your country, we ensure it is protected using industry-standard safeguards and in compliance with applicable laws and data protection regulations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                11. Your Rights
              </h2>
              <p style={{ color: colors.secondary }}>
                You have the right to access, update, or delete your personal data, withdraw your consent, or object to how your information is processed. You may also request data portability or lodge a complaint with a supervisory authority. For any such requests, please contact us using the information provided below.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                12. Third-Party Links
              </h2>
              <p style={{ color: colors.secondary }}>
                Our website may contain links to third-party websites and services. This Privacy Policy does not apply to those third parties, and we are not responsible for their privacy practices. We encourage you to read their policies before sharing any personal data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                13. Policy Updates
              </h2>
              <p style={{ color: colors.secondary }}>
                We may revise this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. Continued use of our services after such updates constitutes acceptance of the revised terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: colors.foreground }}>
                14. Contact Us
              </h2>
              <p style={{ color: colors.secondary }}>
                If you have questions or concerns about this Privacy Policy or your personal data, you may contact Agape Entertainment at{" "}
                <a href="mailto:info@agapeentertainment.in" className="underline" style={{ color: colors.primary }}>
                  info@agapeentertainment.in
                </a>
              </p>
            </section>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
